import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

/**
 * New Relic Browser Agent Integration
 * This file provides utility functions for interacting with the agent.
 */

const licenseKey = import.meta.env.VITE_NEW_RELIC_LICENSE_KEY;
const appId = import.meta.env.VITE_NEW_RELIC_APP_ID;
const accountID = import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID;
const trustKey = import.meta.env.VITE_NEW_RELIC_TRUST_ID;

interface NewRelicAPI {
  addPageAction: (name: string, attributes?: Record<string, unknown>) => void;
  noticeError: (error: Error, customAttributes?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    newrelic?: NewRelicAPI;
    NREUM?: unknown;
  }
}

const options = {
  info: {
    beacon: 'bam.nr-data.net',
    errorBeacon: 'bam.nr-data.net',
    licenseKey,
    applicationID: appId,
    sa: 1,
  },
  loader_config: {
    accountID,
    trustKey,
    agentID: appId,
    licenseKey,
    applicationID: appId,
  },
  init: {
    ajax: { enabled: true, deny_list: ['bam.nr-data.net'] },
    jserrors: { enabled: true },
    page_action: { enabled: true },

    // Performance monitoring
    metrics: { enabled: true },
    page_view_event: { enabled: true },
    page_view_timing: { enabled: true },

    // Disable heavy features for better performance
    session_trace: { enabled: false },
    spa: { enabled: false },
    distributed_tracing: { enabled: false },
    session_replay: { enabled: false },

    privacy: {},
  },
};

// Initialize browser agent asynchronously to prevent blocking
let browserAgent: BrowserAgent | null = null;

function initializeBrowserAgent() {
  try {
    browserAgent = new BrowserAgent(options);

    browserAgent.start().catch((error: unknown) => {
      console.warn('New Relic agent failed to start:', error);
    });
  } catch (error) {
    console.warn('Failed to initialize New Relic browser agent:', error);
  }
}

// Use requestIdleCallback to initialize when browser is idle
function initializeWhenIdle() {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => {
      initializeBrowserAgent();
    });
  } else {
    // Fallback for browsers without requestIdleCallback
    setTimeout(initializeBrowserAgent, 0);
  }
}

// Initialize after page load to avoid blocking
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeWhenIdle);
} else {
  initializeWhenIdle();
}

/**
 * Record an event
 */
export function recordEvent(eventName: string, attributes: Record<string, unknown> = {}): void {
  try {
    if (window.newrelic?.addPageAction) {
      window.newrelic.addPageAction(eventName, attributes || {});
    } else {
      console.warn(`New Relic not available - event not sent: ${eventName}`);
    }
  } catch (error) {
    console.warn('Failed to record New Relic event:', error);
  }
}

/**
 * Record an error
 */
export function recordError(error: Error, customAttributes?: Record<string, unknown>): void {
  try {
    if (window.newrelic?.noticeError) {
      window.newrelic.noticeError(error, customAttributes);
    } else {
      console.error('New Relic not available - error not sent:', error.message, customAttributes);
    }
  } catch (newRelicError) {
    console.error('Failed to send error to New Relic:', newRelicError);
    console.error('Original error:', error.message, customAttributes);
  }
}
